import Input from "components/EvtKit/input/Input"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"

import { ChangeElement, ListElement } from "hooks/useHandleObjList"
import { selectors } from "pages/purchase-plans/constants/defaultValues"

import { memo, useCallback } from "react"
import styled from "styled-components"
import EventTypeSelector from "./selectors/EventTypeSelector"

const PricingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
`

const InlineGridContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
  gap: 16px;
`

const InlineFlexContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
`

const InputContainer = styled.div`
  height: 48px;

  &.isVariable {
    width: 354px;
  }
`

const SwitchContainer = styled.div`
  display: flex;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.disabled};
`

interface PricingElementProps {
  item: ListElement<Partial<App.Pricings>>
  changeElement: ChangeElement<App.Pricings>
}

const PricingExpressionElement = (props: PricingElementProps) => {
  const { item, changeElement } = props

  const onSelectorChange = useCallback(
    (key: string, name: "source" | "scopeType" | "parameter") => (e: Option | null) => {
      const optionValue = e?.value ?? ""

      changeElement(key, name, optionValue)
    },
    []
  )

  return (
    <PricingContainer key={item.key}>
      <InlineFlexContainer>
        {item.scopeType === "event-type" && (
          <>
            <SwitchContainer>
              <SwitchV2
                label="É uma variável de quota?"
                value={!!item.isQuota}
                onChange={() => changeElement(item.key, "isQuota", !item.isQuota)}
                variant="medium"
                grayBackground
              />
            </SwitchContainer>

            {item.isQuota === true && (
              <>
                <SwitchContainer>
                  <SwitchV2
                    label="Pode transbordar?"
                    value={!!item.canOverflow}
                    onChange={() => changeElement(item.key, "canOverflow", !item.canOverflow)}
                    variant="medium"
                    grayBackground
                  />
                </SwitchContainer>

                <Input
                  type="number"
                  name="quotaSize"
                  label="Quantidade de quota"
                  className="fullWidth"
                  value={item.quotaSize}
                  onChange={(e) => {
                    changeElement(item.key, "quotaSize", e.target.value)
                  }}
                  shrink
                  placeholder="Digite"
                />
              </>
            )}
          </>
        )}

        <InputContainer className="isVariable">
          <Input
            type="text"
            name="variableNameInExpression"
            label="Variável"
            className="fullWidth"
            value={item.variableNameInExpression}
            readOnly
            shrink
          />
        </InputContainer>
      </InlineFlexContainer>

      <InlineGridContainer>
        <SelectV2
          className="fullWidth"
          id="scopeType"
          label="Tipo de escopo *"
          options={selectors.scopeTypeOptionsExpression}
          placeholder="Selecione"
          shrink
          value={item.scopeType || ""}
          onChange={onSelectorChange(item.key, "scopeType")}
        />

        {item.scopeType === "event-type" && (
          <EventTypeSelector onSelectorChange={onSelectorChange(item.key, "source")} value={item.source} />
        )}

        {item.scopeType === "event-type" && (
          <SelectV2
            className="fullWidth"
            id="parameter"
            label="Parâmetro *"
            options={selectors.parametersOptionsExpression}
            placeholder="Selecione"
            shrink
            value={item.parameter || ""}
            onChange={onSelectorChange(item.key, "parameter")}
          />
        )}
      </InlineGridContainer>

      {item.parameter === "checkoutSelection" && (
        <InlineGridContainer>
          <InputContainer>
            <Input
              type="text"
              name="variableLabelForDisplay"
              label="Nome de exibição"
              className="fullWidth"
              value={item.variableLabelForDisplay}
              onChange={(e) => {
                changeElement(item.key, "variableLabelForDisplay", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>

          <InputContainer>
            <Input
              type="number"
              name="min"
              label="Valor mínimo"
              className="fullWidth"
              value={item.min}
              onChange={(e) => {
                changeElement(item.key, "min", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>

          <InputContainer>
            <Input
              type="number"
              name="max"
              label="Valor máximo"
              className="fullWidth"
              value={item.max}
              onChange={(e) => {
                changeElement(item.key, "max", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>
        </InlineGridContainer>
      )}

      <Divider />
    </PricingContainer>
  )
}

export default memo(PricingExpressionElement)
